import { motion, useAnimationFrame, useMotionValue, useTransform, wrap } from "framer-motion";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import React, { useCallback, useMemo } from "react";

import { useWindowSize } from "../../../hooks/use-window-size";

export interface VerticalImage {
  img?: IGatsbyImageData | null;
  backgroundColor?: string | null;
}

export interface VerticalSliderProps {
  items: VerticalImage[];
  baseVelocity?: number;
  containerClassName?: string;
}

export function VerticalSlider({
  items,
  baseVelocity = 100,
  containerClassName,
}: VerticalSliderProps) {
  const { height } = useWindowSize();

  const imgHeight = height / 2;
  const imgWidth = (imgHeight / 4) * 3;
  const baseY = useMotionValue(imgHeight * items.length);

  useAnimationFrame((_, delta) => {
    const moveBy = baseVelocity * (delta / 1000);
    baseY.set(baseY.get() + moveBy);
  });

  const redundantItems = useMemo(() => {
    return items.concat(items).concat(items);
  }, [items]);

  const transformPosition = useCallback(
    (v: number) => `${wrap(-imgHeight * items.length, 0, v - imgHeight * items.length)}px`,
    [imgHeight, items]
  );

  const y = useTransform(baseY, transformPosition);

  const commonClasses = "rounded-3xl w-full overflow-hidden";
  return (
    <motion.div style={{ y }} className={containerClassName}>
      {redundantItems
        .filter((img) => img.backgroundColor || img.img)
        .map(({ img, backgroundColor }, i) => (
          <div
            className={commonClasses}
            style={{
              backgroundColor: backgroundColor ?? undefined,
              height: imgHeight,
              width: imgWidth,
            }}
            key={i}
          >
            {img && (
              <GatsbyImage
                objectFit="cover"
                style={{
                  backgroundColor: backgroundColor ?? undefined,
                  height: imgHeight,
                  width: imgWidth,
                }}
                image={img}
                alt=""
              />
            )}
          </div>
        ))}
    </motion.div>
  );
}
